import { useState, useEffect, useCallback, useRef } from "react";
import { Upload, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLang } from "@/lib/i18n";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const AdminGallery = ({ assoc }) => {
  const { t } = useLang();
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/associations/${assoc.slug}/gallery`).then(({ data }) => setPhotos(data));
  }, [assoc.slug]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      await api.post(`/associations/${assoc.slug}/gallery`, fd);
      toast.success(t("saved"));
      setCaption("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/associations/${assoc.slug}/gallery/${id}`);
    load();
  };

  return (
    <div>
      <h1 className="font-heading font-extrabold text-2xl tracking-tight mb-8">{t("gallery")}</h1>
      <div className="border border-border rounded-lg bg-card p-5 mb-8 flex flex-col sm:flex-row gap-3">
        <Input data-testid="gallery-caption-input" placeholder={t("caption")} value={caption} onChange={(e) => setCaption(e.target.value)} className="flex-1" />
        <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="gallery-file-input" />
        <Button data-testid="gallery-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full active:scale-95">
          <Upload className="h-4 w-4 mr-2" /> {uploading ? "..." : t("upload_photo")}
        </Button>
      </div>
      {photos.length === 0 ? (
        <p className="text-sm text-muted-foreground py-12 text-center border border-dashed border-border rounded-lg">{t("no_photos")}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="admin-gallery-grid">
          {photos.map((p) => (
            <div key={p.id} className="relative group rounded-lg overflow-hidden border border-border aspect-square">
              <img src={`${BACKEND}/api/gallery/${p.id}/image`} alt={p.caption || "photo"} loading="lazy" className="w-full h-full object-cover" />
              <Button variant="destructive" size="icon" data-testid="gallery-delete-btn" onClick={() => remove(p.id)}
                className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
              {p.caption && <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-2 py-1.5 truncate">{p.caption}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
